import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  getListingNotesClient,
  makeOptionallyAuthenticatedQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { ListingNoteDto } from '@b-stock/saved-search-api-client'

/**
 * Root of the listing-notes query-key family. The single and the batch reads
 * both key under it, so invalidating `[LISTING_NOTES_QUERY_KEY]` refreshes
 * every listing-notes read at once.
 */
export const LISTING_NOTES_QUERY_KEY = 'listing-notes'

/** The saved-search batch read accepts at most this many listing ids. */
export const LISTING_NOTES_BATCH_SIZE = 100

/** Active note text keyed by listing id; listings without a note are absent. */
export type ListingNotesByListingId = Record<string, string>

const makeListingNotesKey = (name: string, args: unknown[]) => [
  LISTING_NOTES_QUERY_KEY,
  name,
  ...args,
]

/** The buyer's active note on one listing, or null when there is none (404). */
const getListingNote = async (
  accessToken: string | null,
  personId: string,
  listingId: string
): Promise<ListingNoteDto | null> => {
  try {
    const { data } = await getListingNotesClient().getListingNote(
      { personId, listingId },
      buildAxiosRequestConfig({ accessToken })
    )
    return data
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

/**
 * The buyer's active notes for a set of listings, requested in chunks of
 * {@link LISTING_NOTES_BATCH_SIZE} ids and merged into one lookup.
 */
const getListingNotes = async (
  accessToken: string | null,
  personId: string,
  listingIds: string[]
): Promise<ListingNotesByListingId> => {
  if (listingIds.length === 0) return {}
  const client = getListingNotesClient()
  const axiosOptions = buildAxiosRequestConfig({ accessToken })
  const responses = await Promise.all(
    chunk(listingIds, LISTING_NOTES_BATCH_SIZE).map((ids) =>
      client.getListingNotes(
        { personId, listingIds: ids.join(',') },
        axiosOptions
      )
    )
  )
  return Object.fromEntries(
    responses.flatMap(({ data }) =>
      data.listingNotes.map((listingNote) => [
        listingNote.listingId,
        listingNote.note,
      ])
    )
  )
}

const listingNoteQuery = makeOptionallyAuthenticatedQuery<
  [string, string],
  ListingNoteDto | null
>('single', getListingNote, { makeKey: makeListingNotesKey })

const listingNotesQuery = makeOptionallyAuthenticatedQuery<
  [string, string[]],
  ListingNotesByListingId
>('batch', getListingNotes, { makeKey: makeListingNotesKey })

/**
 * The buyer's active note on one listing (null when none). Makes no request
 * without a person, e.g. signed out or a read-only surface.
 */
const useListingNote = (personId: string | undefined, listingId: string) =>
  listingNoteQuery.useWithOpts([personId ?? '', listingId], {
    enabled: !!personId && !!listingId,
  })

/** De-duplicated and sorted, so the same set of ids shares one cache entry. */
const toBatchKeyIds = (listingIds: string[]) => [...new Set(listingIds)].sort()

/**
 * The person a listing-notes key reads for: keys are
 * `[LISTING_NOTES_QUERY_KEY, name, accessToken, personId, ...]`.
 */
const personIdOfKey = (queryKey: readonly unknown[]) => queryKey[3]

/** The entries of `notes` whose listing id is in `listingIds`. */
const pickListingNotes = (
  notes: ListingNotesByListingId,
  listingIds: string[]
): ListingNotesByListingId =>
  Object.fromEntries(
    listingIds.flatMap((listingId) =>
      notes[listingId] === undefined ? [] : [[listingId, notes[listingId]]]
    )
  )

/**
 * A `placeholderData` that keeps the last read's notes for the listings in
 * `listingIds`, and only when that read was for the same person.
 */
const keepNotesOfStayingListings =
  (personId: string | undefined, listingIds: string[]) =>
  (
    previousNotes: ListingNotesByListingId | undefined,
    previousQuery: { queryKey: readonly unknown[] } | undefined
  ) =>
    previousNotes &&
    personId &&
    previousQuery &&
    personIdOfKey(previousQuery.queryKey) === personId
      ? pickListingNotes(previousNotes, listingIds)
      : undefined

type UseListingNotesOptions = {
  /**
   * While the read for a new set of ids loads, return the notes the last read
   * found for the listings still in the set, so a page change or a removal
   * does not blank the notes on cards that stay. Never one listing's note on
   * another, and never another person's notes.
   */
  keepsPreviousNotes?: boolean
}

/**
 * The buyer's active notes for a set of listings, keyed by listing id. Ids are
 * de-duplicated and sorted so the same set shares one cache entry.
 */
const useListingNotes = (
  personId: string | undefined,
  listingIds: string[],
  { keepsPreviousNotes = false }: UseListingNotesOptions = {}
) => {
  const sortedIds = toBatchKeyIds(listingIds)
  return listingNotesQuery.useWithOpts([personId ?? '', sortedIds], {
    enabled: !!personId && sortedIds.length > 0,
    placeholderData: keepsPreviousNotes
      ? keepNotesOfStayingListings(personId, sortedIds)
      : undefined,
  })
}

/**
 * The buyer's active notes for a set of listings, read through the same query
 * as {@link useListingNotes} with the same ids: a refetch already in flight is
 * joined, otherwise the notes are fetched now. Rejects when the read fails.
 */
const fetchListingNotes = (
  queryClient: QueryClient,
  accessToken: string | null,
  personId: string,
  listingIds: string[]
): Promise<ListingNotesByListingId> =>
  listingNotesQuery.prefetch(personId, toBatchKeyIds(listingIds))(
    queryClient,
    accessToken
  )

/**
 * The buyer's active note text on one listing, read through the same query as
 * {@link useListingNote}: a fresh cached read is reused, a missing, stale or
 * invalidated one is fetched. Undefined when there is none (404). Rejects when
 * the read fails.
 */
const fetchListingNote = async (
  queryClient: QueryClient,
  accessToken: string | null,
  personId: string,
  listingId: string
): Promise<string | undefined> => {
  const listingNote = await listingNoteQuery.prefetch(personId, listingId)(
    queryClient,
    accessToken
  )
  return listingNote?.note || undefined
}

/**
 * Refreshes every listing-notes read (single and batch). Call it after any
 * write that can change a note server-side.
 */
const invalidateListingNotes = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: [LISTING_NOTES_QUERY_KEY] })

type ListingNoteTarget = {
  personId: string
  listingId: string
}

/**
 * Options for a hook that writes notes. `refreshesListingNotes: false` leaves
 * the listing-notes refetch to a caller that writes many listings at once and
 * refreshes the reads once after all of them.
 */
export type ListingNoteWriteOptions = {
  refreshesListingNotes?: boolean
}

export type PutListingNoteParams = ListingNoteTarget & {
  accountId: string
  note: string
}

/**
 * Creates or edits the buyer's note; never changes the Watchlist. Resolves
 * once the listing-notes reads have refetched, unless told to leave that to
 * the caller.
 */
const usePutListingNote = ({
  refreshesListingNotes = true,
}: ListingNoteWriteOptions = {}) => {
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      personId,
      listingId,
      accountId,
      note,
    }: PutListingNoteParams) => {
      const { status, statusText } =
        await getListingNotesClient().putListingNote(
          { personId, listingId, putListingNoteDto: { accountId, note } },
          buildAxiosRequestConfig({ accessToken: accessToken || '' })
        )
      return { status, statusText }
    },
    onSuccess: () =>
      refreshesListingNotes ? invalidateListingNotes(queryClient) : undefined,
  })
}

/**
 * Clears the buyer's note (soft delete); never changes the Watchlist.
 * Resolves once the listing-notes reads have refetched.
 */
const useClearListingNote = () => {
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ personId, listingId }: ListingNoteTarget) => {
      const { status, statusText } =
        await getListingNotesClient().deleteListingNote(
          { personId, listingId },
          buildAxiosRequestConfig({ accessToken: accessToken || '' })
        )
      return { status, statusText }
    },
    onSuccess: () => invalidateListingNotes(queryClient),
  })
}

export {
  fetchListingNote,
  fetchListingNotes,
  getListingNote,
  getListingNotes,
  invalidateListingNotes,
  useListingNote,
  useListingNotes,
  usePutListingNote,
  useClearListingNote,
}
